/* eslint-disable @typescript-eslint/no-explicit-any */

import { submitFormJson, postJsonData, getJSONByUrl } from 'widgets/toolbox/ajax';
import { createAdyenComponent, getAdyenPackage, initializeAdyenCheckout } from '../toolbox/adyen';
import { TGTMInfo } from 'types/models/product';
import { startBeforeUnloadGuard, stopBeforeUnloadGuard } from 'core/toolbox/beforeUnloadGuard';
import { requestPaymentMethods, TAdyenPaymentMethods, TApplePayConfig } from './service/adyenPaymentMethods';
import { isMember } from 'core/utils';

const APPLE_PAY = 'applepay';
const APPLE_PAY_PDP = 'applepay-pdp';

function getAdyenWebEnvironment(environment: string): string {
    return environment.toLowerCase();
}

type TAnyObject = Record<string, any>;

type TCost = {
    value: number;
    currency: string;
};

type TShippingMethod = {
    displayName: string;
    description: string;
    ID: string;
    shippingCost: TCost;
    shipmentUUID: string;
    default?: boolean;
    selected?: boolean;
};

type TShippingMethodsData = {
    shippingMethods: Array<TShippingMethod>;
};

declare global {
    interface Window {
        loadedExpressMethods: Array<string>;
    }
}

/**
 * @param Widget Base widget for extending
 * @returns ApplePayExpress Class
 */
function ApplePayExpressClassCreator(Widget: import('widgets/Widget').TWidget) {
    /**
     * @category widgets
     * @subcategory cart, pdp
     * @class ApplePayExpress
     * @augments Widget
     * @classdesc ApplePayExpress widget
     */
    class ApplePayExpress extends Widget {
        shippingMethodsData: TShippingMethodsData | undefined;

        basketAmount = 0;

        isCartError = this.prefs().isCartError;

        paymentMethods: TAdyenPaymentMethods = this.prefs().adyenPaymentMethods as TAdyenPaymentMethods;

        product = this.prefs().product;

        productOptions: Array<Record<string, unknown> | null> = [];

        isBOPISSelected = false;

        currentGtmInfo?: TGTMInfo;

        applePayStateData: TAnyObject | null = null;

        mountedApplePayButtons: Array<TAnyObject> = [];

        prefs() {
            return {
                clientKey: '',
                locale: '',
                countryCode: '',
                environment: '',
                digitsNumber: '',
                buttonColor: 'white-outline',
                basketAmount: { value: 0, currency: '' },
                createTempBasketUrl: '',
                restoreStoredBasketUrl: '',
                calculateAmountUrl: '',
                paymentFromComponentUrl: '',
                adyenPaymentMethods: [] as TAdyenPaymentMethods,
                expressPaymentMethodsUrl: '',
                shippingMethodsUrl: '',
                showConfirmationAction: '',
                product: {
                    id: '',
                    selectedQuantity: 1,
                    readyToOrder: false,
                    available: false,
                    membersOnly: false
                },
                isLoyaltyEnrollmentRequired: false,
                isCartError: false,
                persistentExpressPayments: false,
                ...super.prefs()
            };
        }

        /**
         * @description Check if button container should be shown
         */
        isShowButtonContainer(): boolean {
            const isSignInToBuy = this.product?.membersOnly && !isMember(this.prefs().isLoyaltyEnrollmentRequired);

            return Boolean(
                (this.product?.readyToOrder || this.prefs().persistentExpressPayments) &&
                    this.product?.available &&
                    !isSignInToBuy &&
                    !this.isBOPISSelected
            );
        }

        /**
         * @description Initialize widget logic
         */
        init() {
            this.eventBus().on('cart.updated', 'mountApplePayButton');
            this.eventBus().on('minicart.modal.shown', 'mountApplePayButton');
            this.eventBus().on('giftcertificate.updated', 'mountApplePayButton');
            this.eventBus().on('product.updated', 'updateProductData');
            this.eventBus().on('quantity.changed', 'updateProductQuantity');
            this.eventBus().on('delivery.changed', 'updateDeliveryMethod');
            this.eventBus().on('product.personalization.changed', 'updateProductOptions');
            this.mountApplePayButton(null);

            if (this.isShowButtonContainer()) {
                this.ref(APPLE_PAY_PDP).show();
            }

            this.onDestroy(() => {
                stopBeforeUnloadGuard();
            });
        }

        /**
         * @description Update product data
         * @param response Update product response
         */
        updateProductData(response: TAnyObject): void {
            if (this.prefs().product.id) {
                const { product } = response;

                if (product) {
                    this.product = {
                        ...this.product,
                        id: product.id,
                        readyToOrder: product.readyToOrder,
                        available: product.available,
                        membersOnly: product.membersOnly
                    };

                    this.currentGtmInfo = product.gtmInfo;
                }

                if (this.isShowButtonContainer()) {
                    this.ref(APPLE_PAY_PDP).show();
                } else {
                    this.ref(APPLE_PAY_PDP).hide();
                }
            }
        }

        /**
         * @description Update product quantity
         * @param quantity Product quantity
         */
        updateProductQuantity(quantity: string): void {
            this.product.selectedQuantity = Number.parseInt(quantity, 10) || 1;
        }

        /**
         * @description Update selected product options
         * @param options Selected product options
         */
        updateProductOptions(options: Array<Record<string, unknown> | null>): void {
            this.productOptions = options || [];
        }

        /**
         * @description Update product delivery method
         * @param isBOPIS is BOPIS delivery method selected
         */
        updateDeliveryMethod(isBOPIS: boolean): void {
            this.isBOPISSelected = isBOPIS;

            if (this.isShowButtonContainer()) {
                this.ref(APPLE_PAY_PDP).show();
            } else {
                this.ref(APPLE_PAY_PDP).hide();
            }
        }

        /**
         * @description Loads payment methods for express checkout if not preloaded
         */
        async ensurePaymentMethodsLoaded(): Promise<void> {
            if (this.paymentMethods?.length) {
                return;
            }

            const paymentMethodsUrl = this.prefs().expressPaymentMethodsUrl;

            if (!paymentMethodsUrl) {
                this.paymentMethods = [];

                return;
            }

            const response = await requestPaymentMethods(paymentMethodsUrl);

            this.paymentMethods = response?.adyenPaymentMethods ?? [];
        }

        /**
         * @description Prepare Adyen SDK configuration
         * @returns Configuration object
         */
        getAdyenConfiguration() {
            return {
                locale: this.prefs().locale,
                clientKey: this.prefs().clientKey,
                environment: getAdyenWebEnvironment(this.prefs().environment),
                countryCode: this.prefs().countryCode,
                amount: {
                    value: (this.basketAmount && this.getMinorUnits(this.basketAmount)) || this.prefs().basketAmount.value,
                    currency: this.prefs().basketAmount.currency
                }
            };
        }

        /**
         * @description Create Apple Pay button
         * @param adyenCheckoutInstance AdyenCheckout instance
         * @param applePayButtonConfig Apple Pay button configuration
         * @returns Apple Pay button
         */
        async createApplePayButton(adyenCheckoutInstance, applePayButtonConfig) {
            const adyenPackage = await getAdyenPackage();

            return createAdyenComponent(adyenPackage, adyenCheckoutInstance, APPLE_PAY, applePayButtonConfig);
        }

        /**
         * @description Format Customer Object
         * @param customerData Customer Data
         * @param billingData Billing Data
         * @returns Formated Customer Object
         */
        formatCustomerObject(customerData, billingData) {
            return {
                addressBook: {
                    addresses: {},
                    preferredAddress: {
                        address1: customerData.addressLines[0],
                        address2: customerData.addressLines.length > 1 ? customerData.addressLines[1] : null,
                        city: customerData.locality,
                        countryCode: {
                            displayValue: customerData.country,
                            value: customerData.countryCode
                        },
                        firstName: customerData.givenName,
                        lastName: customerData.familyName,
                        ID: customerData.emailAddress,
                        postalCode: customerData.postalCode,
                        stateCode: customerData.administrativeArea
                    }
                },
                billingAddressDetails: {
                    address1: billingData.addressLines[0],
                    address2: billingData.addressLines.length > 1 ? billingData.addressLines[1] : null,
                    city: billingData.locality,
                    countryCode: {
                        displayValue: billingData.country,
                        value: billingData.countryCode
                    },
                    firstName: billingData.givenName,
                    lastName: billingData.familyName,
                    postalCode: billingData.postalCode,
                    stateCode: billingData.administrativeArea
                },
                customer: {},
                profile: {
                    firstName: customerData.givenName,
                    lastName: customerData.familyName,
                    email: customerData.emailAddress,
                    phone: customerData.phoneNumber
                }
            };
        }

        /**
         * @description Handle Authorised Response
         * @param response Payment Response
         * @param resolveApplePay Resolve ApplePay method
         */
        handleAuthorised(response, resolveApplePay) {
            response.confirmationFormData.result = JSON.stringify({
                pspReference: response.fullResponse?.pspReference,
                resultCode: response.fullResponse?.resultCode,
                paymentMethod: response.fullResponse?.paymentMethod
                    ? response.fullResponse.paymentMethod
                    : response.fullResponse?.additionalData?.paymentMethod,
                donationToken: response.fullResponse?.donationToken,
                amount: response.fullResponse?.amount,
                additionalData: response.fullResponse?.additionalData
            });

            resolveApplePay({
                resultCode: response.fullResponse?.resultCode || response.resultCode,
                action: response.fullResponse?.action || response.action,
                order: response.fullResponse?.order || response.order,
                donationToken: response.fullResponse?.donationToken || response.donationToken
            });

            this.submitConfirmationForm(response);
        }

        /**
         * @description Handle Error Response
         * @param response Payment Response
         * @param rejectApplePay reject ApplePay method
         */
        handleError(response, rejectApplePay) {
            response.confirmationFormData.result = JSON.stringify({ error: true });

            rejectApplePay({
                error: {
                    applePayError: this.getApplePayErrors([{ code: 'unknown', message: response.errorMessage }])
                }
            });

            this.submitConfirmationForm(response);
        }

        /**
         * @description Handle Cancel Error
         * @param error Error Response
         */
        handleCancel(error) {
            if (error?.name === 'CANCEL' && this.product.id && this.prefs().restoreStoredBasketUrl) {
                getJSONByUrl(this.prefs().restoreStoredBasketUrl);
            }
        }

        /**
         * @description Handle Validation Fields
         * @param response Payment Response
         * @param rejectApplePay Reject ApplePay method
         */
        handleValidationFields(response, rejectApplePay) {
            rejectApplePay({
                error: {
                    applePayError: this.getApplePayErrors(response.errors)
                }
            });
        }

        /**
         * @description Handle ApplePay Response
         * @param response Response
         * @param resolveApplePay Resolve ApplePay method
         * @param rejectApplePay Reject ApplePay method
         */
        handleApplePayResponse(response, resolveApplePay, rejectApplePay) {
            if (response.resultCode === 'Authorised') {
                this.handleAuthorised(response, resolveApplePay);
            } else if (response.resultCode === 'ValidationError') {
                this.handleValidationFields(response, rejectApplePay);
            } else {
                this.handleError(response, rejectApplePay);
            }
        }

        /**
         * @description Returns array of Apple Pay Errors
         * @param errors Arraywithdata for ApplePayError class
         * @returns array of Apple Pay Errors
         */
        getApplePayErrors(errors) {
            // eslint-disable-next-line @typescript-eslint/no-array-constructor
            const validationErrors = new Array();

            if (errors) {
                errors.forEach(err => {
                    const newError = new ApplePayError(err.code, err.contactField, err.message);

                    validationErrors.push(newError);
                });
            }

            return validationErrors;
        }

        /**
         * @description Call PaymentFromComponent endpoint
         * @param data Payment Data
         * @param resolve Resolve ApplePay method
         * @param reject Reject ApplePay method
         */
        callPaymentFromComponent(data, resolve, reject) {
            let expressCheckoutEntryPoint;

            if (window.contexts.includes('pdp')) {
                expressCheckoutEntryPoint = 'pdp';
            } else if (window.contexts.includes('cart')) {
                expressCheckoutEntryPoint = 'basket';
            } else if (window.contexts.includes('checkout')) {
                expressCheckoutEntryPoint = 'checkout';
            }

            startBeforeUnloadGuard();

            return submitFormJson(this.prefs().paymentFromComponentUrl, {
                data: JSON.stringify(data),
                paymentMethod: APPLE_PAY,
                expressCheckoutEntryPoint
            })
                .then(response => {
                    response.confirmationFormData = {
                        merchantReference: response.orderNo || '',
                        orderToken: response.orderToken || '',
                        additionalDetailsHidden: JSON.stringify(data)
                    };

                    this.handleApplePayResponse(response, resolve, reject);
                    this.applePayStateData = null;
                    stopBeforeUnloadGuard();
                })
                .catch(() => {
                    this.applePayStateData = null;
                    stopBeforeUnloadGuard();
                    reject();
                });
        }

        /**
         * @description Get Minor Units Value
         * @param value Amount of Order
         * @returns Minor Units Value
         */
        getMinorUnits(value) {
            // ** is used instead of Math.pow
            return value * 10 ** Number.parseInt(this.prefs().digitsNumber, 10);
        }

        /**
         * @description Handle Click Event
         * @param resolve Resolve method
         * @param reject Reject method
         */
        handleOnClick(resolve, reject) {
            if (this.isCartError) {
                this.emitWithoutContext('scrolltoerror');

                return;
            }

            if (!this.product.id || !this.product.selectedQuantity) {
                resolve();
                this.eventBus().emit('express.checkout.basket.started');

                return;
            }

            const productData = {
                productId: this.product.id,
                productQuantity: this.product.selectedQuantity.toString(),
                options: JSON.stringify(this.productOptions)
            };

            postJsonData(this.prefs().createTempBasketUrl, productData)
                .then(response => {
                    if (response && !response.error) {
                        this.eventBus().emit('express.checkout.pdp.started');
                        resolve();
                    } else {
                        reject();
                    }
                })
                .catch(() => {
                    reject();
                });
        }

        /**
         * @description Handle Shipping Contact Selected
         * @param applePayConfig ApplePay Merchant Config
         * @param resolve Resolve method
         * @param reject Reject method
         * @param event ApplePay Event
         */
        async handleShippingContactSelected(applePayConfig, resolve, reject, event) {
            const { shippingContact } = event;
            const shippingContactAddress = {
                city: shippingContact.locality,
                countryCode: shippingContact.countryCode,
                stateCode: shippingContact.administrativeArea,
                postalCode: shippingContact.postalCode
            };
            const updatedShippingMethods = await fetch(`${this.prefs().shippingMethodsUrl}?${new URLSearchParams(shippingContactAddress)}`);

            if (!updatedShippingMethods.ok) {
                return reject();
            }

            this.shippingMethodsData = await updatedShippingMethods.json();

            const shippingMethods = this.shippingMethodsData?.shippingMethods || [];
            const isShippingMethodsAvailable = Boolean(shippingMethods.length);
            let shipmentUUID = '';
            let methodID = '';

            if (isShippingMethodsAvailable) {
                const appliedShippingMethod = shippingMethods.find(sm => sm.selected);
                const defaultShippingMethod = shippingMethods.find(sm => sm.default);
                const selectedShippingMethod = appliedShippingMethod || defaultShippingMethod || shippingMethods[0];

                shipmentUUID = selectedShippingMethod.shipmentUUID;
                methodID = selectedShippingMethod.ID;
            }

            const calculationResponse = await fetch(
                `${this.prefs().calculateAmountUrl}?${new URLSearchParams({
                    shipmentUUID,
                    methodID,
                    ...shippingContactAddress
                })}`,
                { method: 'POST' }
            );

            if (!calculationResponse.ok) {
                return reject();
            }

            const newCalculation = await calculationResponse.json();

            // Apple Pay preselects the first shipping method in the list,
            // so the method applied on the basket has to be placed first
            const appliedShippingMethod = shippingMethods.find(sm => sm.ID === methodID);
            const orderedShippingMethods = appliedShippingMethod
                ? [appliedShippingMethod, ...shippingMethods.filter(sm => sm.ID !== methodID)]
                : shippingMethods;
            const shippingMethodsStructured = orderedShippingMethods.map(sm => ({
                label: sm.displayName,
                detail: sm.description || '',
                identifier: sm.ID,
                amount: `${sm.shippingCost.value}`
            }));

            // Update Apple Pay popup with shipping methods and total, or show error if address is not serviceable
            const applePayShippingContactUpdate = {
                newShippingMethods: shippingMethodsStructured,
                newTotal: {
                    type: 'final',
                    label: applePayConfig.merchantName,
                    amount: newCalculation.grandTotalAmount.value
                },
                ...(isShippingMethodsAvailable
                    ? {}
                    : {
                          errors: [new ApplePayError('shippingContactInvalid', 'postalAddress')]
                      })
            };

            return resolve(applePayShippingContactUpdate);
        }

        /**
         * @description Get ApplePay Button Configuration
         * @param applePayConfig ApplePay Merchant Config
         * @param adyenCheckoutInstance AdyenCheckout instance
         * @returns ApplePay Button Configuration
         */
        getApplePayButtonConfig(applePayConfig, adyenCheckoutInstance) {
            const applePayButtonConfig = {
                showPayButton: true,
                isExpress: true,
                configuration: applePayConfig,
                countryCode: adyenCheckoutInstance.options.countryCode,
                amount: adyenCheckoutInstance.options.amount,
                requiredShippingContactFields: ['postalAddress', 'email', 'phone'],
                requiredBillingContactFields: ['postalAddress', 'phone'],
                shippingMethods: [], // is filled in the handleShippingContactSelected method
                buttonColor: this.prefs().buttonColor || 'white-outline',
                onAuthorized: async (data, actions) => {
                    try {
                        this.applePayStateData = null;

                        const event = data.authorizedEvent;
                        const customerData = event.payment.shippingContact;
                        const billingData = event.payment.billingContact;
                        const customer = this.formatCustomerObject(customerData, billingData);
                        const stateData = {
                            paymentMethod: {
                                type: APPLE_PAY,
                                applePayToken: event.payment.token.paymentData
                            },
                            paymentType: 'express',
                            currentUrl: window.location.href,
                            amount: adyenCheckoutInstance.options.amount?.value,
                            merchantName: applePayConfig?.merchantName
                        };

                        this.applePayStateData = { ...stateData, customer };
                        actions.resolve();
                    } catch (error) {
                        actions.reject(error);
                    }
                },
                onClick: async (resolve, reject) => {
                    this.handleOnClick(resolve, reject);
                },
                onSubmit: (state, component, actions) => {
                    if (!actions) {
                        return;
                    }

                    if (!this.applePayStateData) {
                        actions.reject();

                        return;
                    }

                    this.callPaymentFromComponent(this.applePayStateData, actions.resolve, actions.reject);
                },
                onError: error => {
                    this.handleCancel(error);
                },
                onShippingMethodSelected: async (resolve, reject, event) => {
                    const { shippingMethod } = event;
                    const matchingShippingMethod = this.shippingMethodsData?.shippingMethods.find(sm => sm.ID === shippingMethod.identifier);
                    const calculationResponse = await fetch(
                        `${this.prefs().calculateAmountUrl}?${new URLSearchParams({
                            shipmentUUID: matchingShippingMethod?.shipmentUUID ?? '',
                            methodID: matchingShippingMethod?.ID ?? ''
                        })}`,
                        {
                            method: 'POST'
                        }
                    );

                    if (calculationResponse.ok) {
                        const newCalculation = await calculationResponse.json();

                        applePayButtonConfig.amount = {
                            value: newCalculation.grandTotalAmount.value,
                            currency: newCalculation.grandTotalAmount.currency
                        };
                        const applePayShippingMethodUpdate = {
                            newTotal: {
                                type: 'final',
                                label: applePayConfig.merchantName,
                                amount: newCalculation.grandTotalAmount.value
                            }
                        };

                        resolve(applePayShippingMethodUpdate);
                    } else {
                        reject();
                    }
                },
                onShippingContactSelected: async (resolve, reject, event) => {
                    this.handleShippingContactSelected(applePayConfig, resolve, reject, event);
                }
            };

            return applePayButtonConfig;
        }

        /**
         * @description Mount payment method to DOM element adyen-component-container
         * @param cartResponse Response
         */
        async mountApplePayButton(cartResponse) {
            const applepayContainers = this.getApplepayContainers();

            if (!applepayContainers.length) {
                return;
            }

            this.basketAmount = cartResponse?.totals?.grandTotal?.value ?? this.prefs().basketAmount.value;
            this.isCartError = cartResponse ? cartResponse?.valid?.error : this.isCartError;

            // this is to prevent the button from being mounted on the MiniCart and Cart pages
            // when the basket amount is 0
            if (!this.basketAmount && !this.product.id) {
                return;
            }

            await this.ensurePaymentMethodsLoaded();

            initializeAdyenCheckout(this.getAdyenConfiguration())
                .then(async adyenCheckoutInstance => {
                    await this.mountApplePayButtonsOnContainers(adyenCheckoutInstance, applepayContainers);
                })
                .catch(e => {
                    // eslint-disable-next-line no-console
                    console.log(e);
                });
        }

        /**
         * @description Helper to mount Apple Pay buttons on containers
         * @param adyenCheckoutInstance AdyenCheckout instance
         * @param applepayContainers HTMLCollectionOf<Element>
         */
        async mountApplePayButtonsOnContainers(adyenCheckoutInstance, applepayContainers) {
            const applePayPaymentMethod = this.paymentMethods.find(pm => pm.type === APPLE_PAY);

            if (!applePayPaymentMethod) {
                return;
            }

            const applePayConfig = applePayPaymentMethod?.configuration as TApplePayConfig;
            const applePayButtonConfig = this.getApplePayButtonConfig(applePayConfig, adyenCheckoutInstance);

            let applePayButton = await this.createApplePayButton(adyenCheckoutInstance, applePayButtonConfig);

            await applePayButton.isAvailable();

            if (applePayButton) {
                this.mountedApplePayButtons.forEach(button => button.unmount());
                this.mountedApplePayButtons = [];

                for (let expressCheckoutNodesIndex = 0; expressCheckoutNodesIndex < applepayContainers.length; expressCheckoutNodesIndex += 1) {
                    applePayButton = expressCheckoutNodesIndex
                        ? // eslint-disable-next-line no-await-in-loop
                          await this.createApplePayButton(adyenCheckoutInstance, applePayButtonConfig)
                        : applePayButton;
                    applePayButton.mount(applepayContainers[expressCheckoutNodesIndex]);
                    this.mountedApplePayButtons.push(applePayButton);
                }
            }
        }

        /**
         * @description Get Applepay Containers
         * @returns Html Containers for Applepay Button
         */
        getApplepayContainers() {
            return document.getElementsByClassName(this.product.id ? APPLE_PAY_PDP : APPLE_PAY);
        }

        /**
         * @description Submit Confirmation Form
         * @param paymentResponse Payment Response
         */
        submitConfirmationForm(paymentResponse) {
            return submitFormJson(this.prefs().showConfirmationAction, paymentResponse.confirmationFormData).then(response => {
                if (response.redirectUrl) {
                    window.location.assign(response.redirectUrl);
                }
            });
        }
    }

    return ApplePayExpress;
}

export type TApplePayExpress = ReturnType<typeof ApplePayExpressClassCreator>;

export type TApplePayExpressInstance = InstanceType<TApplePayExpress>;

export default ApplePayExpressClassCreator;
